import { ChangeEvent, FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { BulkResult, NetworkType, stock, StockItem, TrackerFilter, TrackerRow, trackers, TrackerState } from '../api';
import { useAuth } from '../auth';
import { ModelSelect } from '../components/Panels';
import { UnitStatusPill } from '../components/UnitStatusPill';
import { ago, dateTime, dayOnly, STATE_LABEL, UNIT_STATUS_LABEL } from '../format';
import { DataGrid } from '../components/DataGrid';

// IMEIs from pasted text: anything 10–17 digits long, one per line or separated by spaces/commas.
const parseImeis = (text: string) => [...new Set(text.match(/\d{10,17}/g) ?? [])];

// A tracker that reported this recently counts as "seen" on the bench check.
const SEEN_MIN = 15;
const seenRecently = (t: TrackerRow) => !!t.lastReceived && Date.now() - Date.parse(t.lastReceived) < SEEN_MIN * 60_000;

export function Trackers() {
  const location = useLocation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const filter: TrackerFilter = {
    q: params.get('q') ?? '',
    state: (params.get('state') ?? '') as TrackerState | '',
    model: params.get('model') ?? '',
    batch: params.get('batch') ?? '',
    selfInstall: (params.get('selfInstall') ?? '') as TrackerFilter['selfInstall'],
  };
  const key = JSON.stringify(filter);
  const [typed, setTyped] = useState(filter.q ?? '');
  const [rows, setRows] = useState<TrackerRow[] | null>(null);
  const [batches, setBatches] = useState<Array<{ id: number; name: string; trackers: number }>>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const setParam = useCallback((name: string, value: string) => {
    setParams((p) => { value ? p.set(name, value) : p.delete(name); return p; }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    const t = setTimeout(() => { if (typed.trim() !== filter.q) setParam('q', typed.trim()); }, 300);
    return () => clearTimeout(t);
  }, [typed, filter.q, setParam]);

  const load = useCallback(() => {
    trackers.list(JSON.parse(key)).then((r) => { setRows(r.trackers); setError(''); }).catch((e) => setError(e.message));
    stock.batches().then((r) => setBatches(r.batches)).catch(() => {});
  }, [key]);
  useEffect(load, [load]);
  // Only trackers still in the list stay ticked.
  useEffect(() => {
    if (rows) setPicked((p) => new Set([...p].filter((id) => rows.some((r) => r.id === id))));
  }, [rows]);

  const back = { from: location.pathname + location.search, fromLabel: 'Trackers' };
  const filtered = !!(filter.state || filter.model || filter.batch || filter.selfInstall);

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Trackers</h1>
        <span className="unit-links">
          <Link to="/install">Install a tracker</Link>
          {user?.role === 'admin' && !adding && <button className="primary" onClick={() => setAdding(true)}>Add to stock</button>}
        </span>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {adding && <AddStock onClose={() => setAdding(false)} onAdded={(batchId) => { if (batchId) setParam('batch', String(batchId)); load(); }} />}

      <div className="toolbar">
        <label className="sr-only" htmlFor="trk-search">Search</label>
        <input id="trk-search" type="search" placeholder="Search IMEI, model, SIM, vehicle, customer, order…" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <label className="sr-only" htmlFor="trk-state">State</label>
        <select id="trk-state" value={filter.state} onChange={(e) => setParam('state', e.target.value)}>
          <option value="">All states</option>
          {Object.entries(STATE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="sr-only" htmlFor="trk-model">Model</label>
        <ModelSelect id="trk-model" value={filter.model || null} onChange={(m) => setParam('model', m ?? '')} emptyLabel="All models" />
        <label className="sr-only" htmlFor="trk-si">Kind</label>
        <select id="trk-si" value={filter.selfInstall} onChange={(e) => setParam('selfInstall', e.target.value)}>
          <option value="">Self-install and fitted by us</option>
          <option value="true">Self-install only</option>
          <option value="false">Fitted by us only</option>
        </select>
        {batches.length > 0 && (
          <>
            <label className="sr-only" htmlFor="trk-batch">Batch</label>
            <select id="trk-batch" value={filter.batch} onChange={(e) => setParam('batch', e.target.value)}>
              <option value="">All batches</option>
              {batches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.trackers})</option>)}
            </select>
          </>
        )}
        {filtered && <button className="link" onClick={() => setParams((p) => { ['state', 'model', 'batch', 'selfInstall'].forEach((k) => p.delete(k)); return p; }, { replace: true })}>Clear filters</button>}
      </div>

      {rows && (
        <DataGrid id="trackers" rows={rows} rowKey={(t) => t.id} noun={['tracker', 'trackers']} href={(t) => `/trackers/${t.id}`} rowState={back}
          empty="No trackers match." defaultSort={{ key: 'imei', dir: 1 }}
          select={{ picked, onChange: setPicked, label: (t) => t.imei }}
          columns={[
            { key: 'imei', label: 'IMEI', always: true, value: (t) => t.imei,
              cell: (t) => <><Link className="mono" to={`/trackers/${t.id}`} state={back}>{t.imei}</Link>
                {t.selfInstall && <> <span className="tag" title={t.hasClaimCode ? 'Has a claim code' : ''}>Self-install</span></>}</> },
            { key: 'model', label: 'Model', value: (t) => t.model },
            { key: 'state', label: 'State', value: (t) => STATE_LABEL[t.state], cell: (t) => <span className={`state state-${t.state}`}>{STATE_LABEL[t.state]}</span> },
            { key: 'vehicle', label: 'Vehicle / order', value: (t) => t.vehicle?.name ?? ([t.orderRef, t.orderEmail].filter(Boolean).join(' · ') || null),
              cell: (t) => (t.vehicle ? <Link to={`/vehicles/${t.vehicle.id}`} state={back}>{t.vehicle.name}</Link>
                : t.orderRef || t.orderEmail ? <span className="small">{[t.orderRef, t.orderEmail].filter(Boolean).join(' · ')}</span>
                : <span className="muted">–</span>) },
            { key: 'customer', label: 'Customer', value: (t) => t.vehicle?.customer?.name ?? null },
            { key: 'status', label: 'Status', value: (t) => (t.status ? UNIT_STATUS_LABEL[t.status] : null),
              cell: (t) => (t.status ? <UnitStatusPill status={t.status} /> : <span className="muted">–</span>) },
            { key: 'last', label: 'Last data', value: (t) => t.lastReceived, sortBy: (t) => (t.lastReceived ? Date.parse(t.lastReceived) : null),
              csv: (t) => (t.lastReceived ? dateTime(t.lastReceived) : null), nowrap: true,
              cell: (t) => (t.lastReceived
                ? <span className={seenRecently(t) && !t.vehicle ? 'seen-ok' : undefined} title={dateTime(t.lastReceived)}>{seenRecently(t) && !t.vehicle && '✓ '}{ago(t.lastReceived)}</span>
                : <span className="muted">never</span>) },
            { key: 'batch', label: 'Batch', hidden: true, value: (t) => t.batch?.name ?? null },
            { key: 'serial', label: 'Serial', hidden: true, value: (t) => t.serial },
            { key: 'firmware', label: 'Firmware', hidden: true, value: (t) => t.firmware },
            { key: 'network', label: 'Network', hidden: true, value: (t) => t.networkType },
            { key: 'sim', label: 'SIM number', hidden: true, value: (t) => t.simNumber, nowrap: true },
            { key: 'iccid', label: 'ICCID', hidden: true, value: (t) => t.simIccid, cell: (t) => (t.simIccid ? <span className="mono">{t.simIccid}</span> : <span className="muted">–</span>) },
            { key: 'added', label: 'Added', hidden: true, value: (t) => t.createdAt, sortBy: (t) => (t.createdAt ? Date.parse(t.createdAt) : null),
              csv: (t) => (t.createdAt ? dayOnly(t.createdAt) : null), cell: (t) => (t.createdAt ? dayOnly(t.createdAt) : <span className="muted">–</span>), nowrap: true },
            { key: 'paid', label: 'Paid until', hidden: true, value: (t) => t.paidUntil, sortBy: (t) => (t.paidUntil ? Date.parse(t.paidUntil) : null),
              csv: (t) => (t.paidUntil ? dayOnly(t.paidUntil) : null), cell: (t) => (t.paidUntil ? dayOnly(t.paidUntil) : <span className="muted">–</span>), nowrap: true },
          ]} />
      )}
      {picked.size > 0 && rows && (
        <SelectionBar rows={rows.filter((r) => picked.has(r.id))} admin={user?.role === 'admin'}
          onClear={() => setPicked(new Set())} onChanged={load} />
      )}
    </div>
  );
}

// --- Actions for the ticked trackers ------------------------------------------------------

const LABEL_SIZE_KEY = 'sprout.labelSize';
function savedLabelSize() {
  try {
    const v = JSON.parse(localStorage.getItem(LABEL_SIZE_KEY) ?? '');
    if (v.widthMm > 0 && v.heightMm > 0) return v as { widthMm: number; heightMm: number };
  } catch { /* nothing saved */ }
  return { widthMm: 62, heightMm: 32 };
}

type Open = null | 'labels' | 'allocated' | 'shipped' | 'stock';

function SelectionBar({ rows, admin, onClear, onChanged }: { rows: TrackerRow[]; admin: boolean; onClear: () => void; onChanged: () => void }) {
  const [open, setOpen] = useState<Open>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [failed, setFailed] = useState<BulkResult>([]);
  const [size, setSize] = useState(savedLabelSize);
  const [orderRef, setOrderRef] = useState('');
  const [orderEmail, setOrderEmail] = useState('');
  const ids = rows.map((r) => r.id);
  const withoutCode = rows.filter((r) => !r.hasClaimCode);
  const imeiOf = useMemo(() => new Map(rows.map((r) => [r.id, r.imei])), [rows]);

  const run = async (what: () => Promise<string | BulkResult>, done: string) => {
    setBusy(true);
    setError('');
    setMessage('');
    setFailed([]);
    try {
      const r = await what();
      if (Array.isArray(r)) {
        const ok = r.filter((x) => !x.error).length;
        setFailed(r.filter((x) => x.error));
        setMessage(`${done}: ${ok} of ${r.length}.`);
        onChanged();
      } else setMessage(r);
      setOpen(null);
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const print = (kind: 'label' | 'insert') => {
    if (kind === 'label') { try { localStorage.setItem(LABEL_SIZE_KEY, JSON.stringify(size)); } catch { /* not saved */ } }
    void run(async () => {
      await stock.print(ids, kind, size);
      return `${kind === 'label' ? 'Labels' : 'Box inserts'} for ${ids.length} tracker${ids.length === 1 ? '' : 's'} downloaded as a PDF.`;
    }, '');
  };

  const codes = (action: 'prepare' | 'renew' | 'undo') => {
    const ask = {
      prepare: `Make ${ids.length} tracker(s) self-install units? Each gets a claim code and QR code.`,
      renew: `Make NEW claim codes for ${ids.length} tracker(s)? Labels and box inserts printed before stop working, so reprint them.`,
      undo: `Turn ${ids.length} tracker(s) back into ordinary stock? Their claim codes and QR codes stop working.`,
    }[action];
    if (!window.confirm(ask)) return;
    void run(async () => (await stock.selfInstall(ids, action)).results,
      { prepare: 'Made self-install units', renew: 'New codes made', undo: 'No longer self-install' }[action]);
  };

  const setState = (e: FormEvent) => {
    e.preventDefault();
    const to = open as 'allocated' | 'shipped' | 'stock';
    void run(async () => (await stock.setState(ids, to, orderRef.trim() || null, orderEmail.trim() || null)).results,
      { allocated: 'Reserved', shipped: 'Marked as shipped', stock: 'Back in stock' }[to]);
  };

  return (
    <div className="selbar" role="region" aria-label="Ticked trackers">
      <strong>{rows.length} ticked</strong>
      <button className="link" onClick={onClear}>Untick all</button>
      <span className="grow" />
      <button onClick={() => setOpen(open === 'labels' ? null : 'labels')} disabled={busy}>Print…</button>
      <button onClick={() => setOpen(open === 'allocated' ? null : 'allocated')} disabled={busy}>Reserve…</button>
      <button onClick={() => setOpen(open === 'shipped' ? null : 'shipped')} disabled={busy}>Mark shipped…</button>
      <button onClick={() => setOpen(open === 'stock' ? null : 'stock')} disabled={busy}>Back to stock…</button>
      {admin && (
        <select aria-label="Self-install" value="" disabled={busy}
          onChange={(e) => { const v = e.target.value as 'prepare' | 'renew' | 'undo'; e.target.value = ''; if (v) codes(v); }}>
          <option value="">Self-install…</option>
          <option value="prepare">Make self-install units</option>
          <option value="renew">Make new claim codes</option>
          <option value="undo">No longer self-install</option>
        </select>
      )}

      {open === 'labels' && (
        <div className="form">
          {withoutCode.length > 0 ? (
            <p className="error">{withoutCode.length} of these {withoutCode.length === 1 ? 'has' : 'have'} no claim code yet
              ({withoutCode.slice(0, 3).map((r) => r.imei).join(', ')}{withoutCode.length > 3 ? '…' : ''}).
              {admin ? ' Make them self-install units first.' : ' Ask an admin to make them self-install units.'}</p>
          ) : (
            <>
              <div className="actions">
                <label>Label width (mm)<input type="number" min={25} max={150} step={1} value={size.widthMm}
                  onChange={(e) => setSize({ ...size, widthMm: Number(e.target.value) })} /></label>
                <label>Length (mm)<input type="number" min={15} max={200} step={1} value={size.heightMm}
                  onChange={(e) => setSize({ ...size, heightMm: Number(e.target.value) })} /></label>
              </div>
              <div className="actions">
                <button className="primary" disabled={busy} onClick={() => print('label')}>Labels ({rows.length})</button>
                <button disabled={busy} onClick={() => print('insert')}>Box inserts, A4 ({rows.length})</button>
              </div>
              <p className="muted small">Each label and insert has the tracker's QR code and claim code. Printing is noted on each tracker's timeline.</p>
            </>
          )}
        </div>
      )}
      {(open === 'allocated' || open === 'shipped' || open === 'stock') && (
        <form className="form" onSubmit={setState}>
          {open !== 'stock' ? (
            <div className="grid3">
              <label>Order reference <span className="muted small">(optional)</span>
                <input type="text" maxLength={100} value={orderRef} onChange={(e) => setOrderRef(e.target.value)} /></label>
              <label>Customer email <span className="muted small">(optional)</span>
                <input type="email" maxLength={200} value={orderEmail} onChange={(e) => setOrderEmail(e.target.value)} /></label>
            </div>
          ) : (
            <p className="small">Reserved or shipped trackers go back into stock (order cancelled, or returned unused). Their order details are cleared.</p>
          )}
          <div className="actions">
            <button className="primary" disabled={busy}>
              {{ allocated: 'Reserve', shipped: 'Mark as shipped', stock: 'Back to stock' }[open]} ({rows.length})
            </button>
            <button type="button" className="link" onClick={() => setOpen(null)}>Cancel</button>
          </div>
        </form>
      )}
      {error && <p className="error" role="alert" style={{ flexBasis: '100%' }}>{error}</p>}
      {message && (
        <div role="status" className="small" style={{ flexBasis: '100%' }}>
          {message}
          {failed.length > 0 && <ul>{failed.map((f) => <li key={f.id}><span className="mono">{f.imei ?? imeiOf.get(f.id!)}</span>: {f.error}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

// --- Add to stock ------------------------------------------------------------------------

// Rows of a CSV with IMEI, serial, ICCID and model. A header row may name the columns in any
// order; without one the columns are taken in that order. Comma, semicolon or tab separated.
function parseCsv(text: string): { items: StockItem[]; problems: string[] } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { items: [], problems: [] };
  const sep = [',', ';', '\t'].sort((a, b) => lines[0].split(b).length - lines[0].split(a).length)[0];
  const split = (l: string) => l.split(sep).map((c) => c.trim().replace(/^"(.*)"$/, '$1').trim());
  const first = split(lines[0]).map((c) => c.toLowerCase());
  const hasHeader = first.some((c) => c.includes('imei'));
  const col = (names: string[], fallback: number) => {
    if (!hasHeader) return fallback;
    return first.findIndex((c) => names.some((n) => c.includes(n)));
  };
  const cols = { imei: col(['imei'], 0), serial: col(['serial', 's/n', 'sn'], 1), iccid: col(['iccid', 'sim'], 2), model: col(['model', 'type'], 3) };
  const items: StockItem[] = [];
  const problems: string[] = [];
  (hasHeader ? lines.slice(1) : lines).forEach((line, i) => {
    const cells = split(line);
    const get = (c: number) => (c >= 0 && cells[c] ? cells[c] : null);
    const imei = (get(cols.imei) ?? '').replace(/\s/g, '');
    if (!/^\d{10,17}$/.test(imei)) { problems.push(`Line ${i + (hasHeader ? 2 : 1)}: "${get(cols.imei) ?? ''}" is not an IMEI`); return; }
    items.push({ imei, serial: get(cols.serial), iccid: get(cols.iccid)?.replace(/\s/g, '') ?? null, model: get(cols.model) });
  });
  return { items, problems };
}

function AddStock({ onClose, onAdded }: { onClose: () => void; onAdded: (batchId: number | null) => void }) {
  const [text, setText] = useState('');
  const [scan, setScan] = useState('');
  const [csv, setCsv] = useState<{ name: string; items: StockItem[]; problems: string[] } | null>(null);
  const [model, setModel] = useState('');
  const [firmware, setFirmware] = useState('');
  const [network, setNetwork] = useState('');
  const [batchName, setBatchName] = useState('');
  const [selfInstall, setSelfInstall] = useState(false);
  const [knownModels, setKnownModels] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<Array<{ imei: string; id?: number; error?: string }> | null>(null);
  const [server, setServer] = useState('');

  useEffect(() => {
    trackers.models().then((r) => setKnownModels(new Set(r.families.flatMap((f) => f.models)))).catch(() => {});
  }, []);

  const items: StockItem[] = csv ? csv.items : parseImeis(text).map((imei) => ({ imei }));
  // Models in the CSV must be ones Sprout knows (they decide how IO values are named).
  const badModels = knownModels ? [...new Set(items.map((i) => i.model).filter((m): m is string => !!m && !knownModels.has(m)))] : [];

  // A barcode scanner types the IMEI and presses Enter: add it to the list and stay in the box.
  const onScan = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const found = parseImeis(scan);
    if (found.length) setText((t) => [t.trim(), ...found].filter(Boolean).join('\n'));
    setScan('');
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 1_000_000) { setError('That file is too big for a tracker list (max 1 MB).'); return; }
    setCsv({ name: file.name, ...parseCsv(await file.text()) });
    if (!batchName) setBatchName(file.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await trackers.add({
        items, model: model || null, firmware: firmware.trim() || null, networkType: (network || null) as NetworkType | null,
        batchName: batchName.trim() || null, selfInstall,
      });
      setResults(r.results);
      setServer(r.trackerServer);
      setText('');
      setCsv(null);
      setBatchName('');
      onAdded(r.batchId);
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  return (
    <form className="card form" onSubmit={submit}>
      <h2>Add trackers to stock</h2>
      <p className="muted small">Each tracker is also created in Traccar, so you can test it on the bench before it's installed or shipped.</p>
      {!csv ? (
        <>
          <label>Scan or type an IMEI <span className="muted small">(a barcode scanner adds each one to the list)</span>
            <input id="stock-scan" type="text" inputMode="numeric" autoComplete="off" value={scan} className="mono"
              onChange={(e) => setScan(e.target.value)} onKeyDown={onScan} placeholder="Scan, or type and press Enter" />
          </label>
          <label>IMEIs <span className="muted small">(one per line, or paste a list; {items.length} found)</span>
            <textarea id="stock-imeis" rows={5} value={text} onChange={(e) => setText(e.target.value)} className="mono" placeholder={'864636067330973\n864636067330981'} />
          </label>
          <label>Or upload a CSV <span className="muted small">(columns IMEI, serial, ICCID, model; a header row is optional)</span>
            <input id="stock-csv" type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => void onFile(e)} />
          </label>
        </>
      ) : (
        <div className="callout">
          <strong>{csv.name}: {csv.items.length} tracker{csv.items.length === 1 ? '' : 's'}</strong>
          {csv.problems.length > 0 && <ul className="small">{csv.problems.slice(0, 10).map((p) => <li key={p}>{p} (skipped)</li>)}</ul>}
          <div className="table-wrap">
            <table className="small">
              <thead><tr><th>IMEI</th><th>Serial</th><th>ICCID</th><th>Model</th></tr></thead>
              <tbody>{csv.items.slice(0, 5).map((i) => <tr key={i.imei}><td className="mono">{i.imei}</td><td>{i.serial ?? '–'}</td><td className="mono">{i.iccid ?? '–'}</td><td>{i.model ?? '–'}</td></tr>)}</tbody>
            </table>
          </div>
          {csv.items.length > 5 && <p className="muted small">…and {csv.items.length - 5} more</p>}
          <div><button type="button" className="link" onClick={() => setCsv(null)}>Use a different file</button></div>
        </div>
      )}
      {badModels.length > 0 && <p className="error">Unknown model{badModels.length > 1 ? 's' : ''} in the file: {badModels.join(', ')}. Use the names from the model list (e.g. FMC920).</p>}
      <div className="grid3">
        <label>Model <span className="muted small">({csv ? 'where the file has none' : 'all of them'})</span><ModelSelect id="stock-model" value={model || null} onChange={(m) => setModel(m ?? '')} /></label>
        <label>Firmware<input id="stock-fw" type="text" maxLength={60} value={firmware} onChange={(e) => setFirmware(e.target.value)} /></label>
        <label>Network
          <select id="stock-net" value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="">–</option>{['2G', '3G', '4G', 'LTE-M', 'NB-IoT'].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>Batch name <span className="muted small">(optional, e.g. delivery)</span>
          <input id="stock-batch" type="text" maxLength={100} value={batchName} onChange={(e) => setBatchName(e.target.value)} /></label>
      </div>
      <label className="check"><input type="checkbox" checked={selfInstall} onChange={(e) => setSelfInstall(e.target.checked)} />
        Self-install units (sold to customers who fit them themselves): give each a claim code and QR code for its label</label>
      {error && <p className="error" role="alert">{error}</p>}
      {results && (
        <div className="callout" role="status">
          <strong>{results.filter((r) => !r.error).length} added to stock.</strong>
          {results.some((r) => r.error) && (
            <ul className="small">{results.filter((r) => r.error).map((r) => <li key={r.imei}><span className="mono">{r.imei}</span>: {r.error}</li>)}</ul>
          )}
          {results.some((r) => !r.error) && <p className="small">Set each tracker's <b>server</b> to <span className="mono">{server}</span> (TCP). For a tracker that also reports to Mapon, use the <b>second server</b> in <b>Duplicate</b> mode. Power it on the bench: once it reports, the list shows <span className="seen-ok">✓</span> under Last data.</p>}
        </div>
      )}
      <div className="actions">
        <button className="primary" disabled={busy || items.length === 0 || badModels.length > 0}>{busy ? 'Adding…' : `Add ${items.length || ''} to stock`}</button>
        <button type="button" className="link" onClick={onClose}>Close</button>
      </div>
    </form>
  );
}
